import { ReactNode } from "react";
import styles from "./AnalyticsState.module.scss";
import CommonLoadingSpinner from "../../loading-spinner/CommonLoadingSpinnerForPage";

type Props = {
	/**
	 * isLoading, not isFetching: only the first load has nothing to show. A background
	 * refetch keeps the figures already on screen rather than blanking the widget.
	 */
	loading: boolean;
	error: boolean;
	/**
	 * The query's own refetch. Without it a failure is a dead end: apiSlice keeps an
	 * unused entry for the default 60s, failed ones included, so leaving the filter and
	 * coming back inside that minute re-serves the cached error and never asks again.
	 */
	onRetry?: () => void;
	/** isFetching — a retry already in flight, so the button says so and cannot be spammed. */
	retrying?: boolean;
	/**
	 * The request succeeded and carried nothing. Distinct from an error and from a
	 * figure of zero, and checked after `loading`, since a query in flight has no data
	 * either and must not read as an empty result.
	 */
	empty?: boolean;
	children: ReactNode;
};

/**
 * What a widget draws in place of its body while its query is in flight or has failed.
 * A failure is not an empty result — without this a 500 reads as "zero of everything",
 * which is the one thing an analytics page must never say by accident. Both notices are
 * bare text: the card around them is frame enough, and its heading names what is missing.
 */
const AnalyticsState = ({ loading, error, onRetry, retrying = false, empty = false, children }: Readonly<Props>) => {
	if (error)
		return (
			<div className={`${styles.notice} ${styles.failed}`} role="alert">
				These figures could not be loaded.
				{onRetry && (
					<button type="button" className={styles.retry} onClick={onRetry} disabled={retrying}>
						{retrying ? "Retrying…" : "Retry"}
					</button>
				)}
			</div>
		);

	return (
		<CommonLoadingSpinner loading={loading} color="var(--app-primary)">
			{empty ? <p className={styles.notice}>No data for this selection.</p> : children}
		</CommonLoadingSpinner>
	);
};

export default AnalyticsState;
